import { type FontSize, useFontSize } from '@/lib/font-size';
import { type Theme, useTheme } from '@/lib/theme';
import {
  Card,
  CardContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@iq/ui';
import { useTranslation } from 'react-i18next';

const THEMES: Array<{ value: Theme; labelKey: string }> = [
  { value: 'system', labelKey: 'settings.general.themeSystem' },
  { value: 'light', labelKey: 'settings.general.themeLight' },
  { value: 'dark', labelKey: 'settings.general.themeDark' },
];

const FONT_SIZES: Array<{ value: FontSize; labelKey: string }> = [
  { value: 'sm', labelKey: 'settings.general.fontSmall' },
  { value: 'md', labelKey: 'settings.general.fontMedium' },
  { value: 'lg', labelKey: 'settings.general.fontLarge' },
];

export function GeneralPanel() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { size, setSize } = useFontSize();

  return (
    <Card data-testid="settings-panel-general">
      <CardContent className="flex flex-col gap-6 py-6">
        <Row label={t('settings.general.theme')}>
          <Select value={theme} onValueChange={(v) => setTheme(v as Theme)}>
            <SelectTrigger className="w-40" data-testid="settings-theme-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THEMES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>

        <Row label={t('settings.general.uiLanguage')}>
          <Select
            value={i18n.language?.startsWith('de') ? 'de' : 'en'}
            onValueChange={(v) => void i18n.changeLanguage(v)}
          >
            <SelectTrigger className="w-40" data-testid="settings-ui-language-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="de">German</SelectItem>
            </SelectContent>
          </Select>
        </Row>

        <Row label={t('settings.general.fontSize')}>
          <Select value={size} onValueChange={(v) => setSize(v as FontSize)}>
            <SelectTrigger className="w-40" data-testid="settings-font-size-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_SIZES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex flex-col gap-0.5">
        <Label className="text-sm font-medium">{label}</Label>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <div className="w-full sm:w-auto [&>button]:w-full sm:[&>button]:w-40">{children}</div>
    </div>
  );
}
